import React, { FunctionComponent } from 'react';
import { Grid, Typography, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import cs from 'classnames';

import { sec2hms } from '/src/utils/time-utils';
import {
  CurrentUserArrow,
  ParticipantArrow,
} from '/src/ui/shared/challengeProtocol/Arrow';
import ChallengeButton from '/src/ui/challengeProtocol/ChallengeButton';
import {
  ChallengeFormula,
  DsqRecord,
  instanceOfDsqRecord,
  instanceOfSingleRaceRecord,
  instanceOfTeamRecord,
  ProtocolRecord,
  SingleRaceRecord,
  TeamRaceRecord,
} from '/src/domain/challenges/ChallengeProtocolData';
import { trimName } from '/src/domain/shared/trimName';
import { useTranslation } from 'react-i18next';
import { User } from '/src/domain/shared/User';
import { formatRecordResultExtended } from '/src/utils/protocol-utils';
import { links } from '/src/domain/shared/links';

import { ParticipantInfo, ParticipantSteps } from './ParticipantInfo';
import { ParticipantBadge, ParticipantBadgeType } from './ParticipantBadge';

const crownIcon = require('/src/assets/icons/crown.svg');

export interface RecordViewProps {
  placeByOrder?: number;
  record: ProtocolRecord;
  isExpanded: boolean;
  isCurrentUserParticipant: boolean;
  isFull: boolean;
  challengeType: ChallengeFormula;
  currentUser: User | undefined;
}

interface TeamRecordViewProps {
  placeByOrder?: number;
  record: TeamRaceRecord;
  isExpanded: boolean;
  isCurrentUserParticipant: boolean;
  isFull: boolean;
  challengeType: ChallengeFormula;
  currentUser: User | undefined;
  profileLink?: string;
}

interface SingleRaceViewProps {
  placeByOrder?: number;
  record: SingleRaceRecord;
  isExpanded: boolean;
  isCurrentUserParticipant: boolean;
  challengeType: ChallengeFormula;
}

interface DsqRecordViewProps {
  record: DsqRecord;
  isExpanded: boolean;
  isCurrentUserParticipant: boolean;
  profileLink?: string;
}

export const RecordView: FunctionComponent<RecordViewProps> = ({
  placeByOrder,
  record,
  isExpanded,
  isCurrentUserParticipant,
  isFull,
  challengeType,
  currentUser,
}) => {
  const profileLink = links.profileLink + '?';

  if (instanceOfTeamRecord(record))
    return (
      <TeamRecordView
        placeByOrder={placeByOrder}
        record={record}
        isExpanded={isExpanded}
        isCurrentUserParticipant={isCurrentUserParticipant}
        isFull={isFull}
        challengeType={challengeType}
        currentUser={currentUser}
        profileLink={profileLink}
      />
    );

  if (instanceOfDsqRecord(record))
    return (
      <DsqRecordView
        record={record}
        isExpanded={isExpanded}
        isCurrentUserParticipant={isCurrentUserParticipant}
        profileLink={profileLink}
      />
    );

  if (instanceOfSingleRaceRecord(record))
    return (
      <SingleRaceRecordView
        placeByOrder={placeByOrder}
        record={record}
        isExpanded={isExpanded}
        isCurrentUserParticipant={isCurrentUserParticipant}
        challengeType={challengeType}
      />
    );

  return null;
};

const SingleRaceRecordView: FunctionComponent<SingleRaceViewProps> = ({
  placeByOrder,
  record,
  isExpanded,
  isCurrentUserParticipant,
  challengeType,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { participant } = record;

  return (
    <Grid
      container
      component={ChallengeButton}
      className={classes.container}
      alignItems="center"
    >
      <Grid item container xs={11} spacing={1}>
        <Grid item container xs={1} className={classes.cell}>
          {isCurrentUserParticipant ? (
            <CurrentUserArrow rotate={isExpanded} />
          ) : (
            <ParticipantArrow rotate={isExpanded} />
          )}
        </Grid>
        <Grid
          item
          xs={1}
          container
          justifyContent="center"
          style={{ paddingLeft: 0 }}
          className={classes.cell}
        >
          {placeByOrder ?? record.place}
        </Grid>
        <Grid item xs={8} className={cs(classes.cell, classes.centeredRow)}>
          <ParticipantInfo participant={record.participant} />
        </Grid>
        <Grid
          item
          container
          xs={2}
          justifyContent="flex-end"
          className={cs(classes.cell, classes.alignEnd)}
        >
          {record.time
            ? sec2hms(record.time)
            : formatRecordResultExtended(challengeType, record.parameters[0].value)}
        </Grid>
      </Grid>
      <Grid item xs={11} className={classes.cell}>
        <div className={classes.badgeContainer}>
          {participant.city && (
            <ParticipantBadge
              type={ParticipantBadgeType.CITY}
              value={participant.city!}
              className={classes.badge}
            />
          )}
          {participant.coach && (
            <ParticipantBadge
              type={ParticipantBadgeType.COACH}
              value={participant.coach!}
              link={
                participant.coachId
                  ? `${links.origin}/profile?${participant.coachId}`
                  : undefined
              }
              className={classes.badge}
            />
          )}
          {participant.team && (
            <ParticipantBadge
              type={ParticipantBadgeType.LOCATION}
              value={participant.team!}
              className={classes.badge}
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
};

const DsqRecordView: FunctionComponent<DsqRecordViewProps> = ({
  record,
  isExpanded,
  isCurrentUserParticipant,
  profileLink,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Grid
      container
      component={ChallengeButton}
      className={classes.container}
      spacing={1}
    >
      <Grid item container alignItems="center" xs={1} className={classes.cell}>
        {isCurrentUserParticipant ? (
          <CurrentUserArrow rotate={isExpanded} />
        ) : (
          <ParticipantArrow rotate={isExpanded} />
        )}
      </Grid>
      <Grid item xs={3} sm={2} className={classes.cell}>
        {record.trackId}
      </Grid>
      <Grid item xs={3} sm={4} className={classes.cell}>
        <Link underline="hover" href={profileLink + record.participant.id}>
          {trimName(record.participant.firstName, record.participant.lastName)}
        </Link>
      </Grid>
      <Grid item xs={4} className={classes.cell}>
        {record.comment && t(record.comment)}
      </Grid>
    </Grid>
  );
};

const MAX_VISIBLE_TEAM_PARTICIPANTS = 5;
const TeamRecordView: FunctionComponent<TeamRecordViewProps> = ({
  placeByOrder,
  record,
  isExpanded,
  isCurrentUserParticipant,
  isFull,
  challengeType,
  currentUser,
  profileLink,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  let visibleRecords = record.records;

  if (record.records.length > MAX_VISIBLE_TEAM_PARTICIPANTS) {
    visibleRecords = record.records
      .filter((r) => r.participant.id === currentUser?.id)
      .concat(
        record.records
          .filter((r) => r.participant.id !== currentUser?.id)
          .slice(0, MAX_VISIBLE_TEAM_PARTICIPANTS),
      );
  }

  return (
    <Grid
      container
      component={ChallengeButton}
      className={cs(
        classes.container,
        !isFull &&
          ![
            ChallengeFormula.TEAM_MAX_DISTANCE,
            ChallengeFormula.TEAM_SIMPLE,
          ].includes(challengeType)
          ? classes.notFinished
          : '',
      )}
      alignItems="center"
    >
      <Grid item container xs={11} spacing={1}>
        <Grid item xs={1} className={cs(classes.cell, classes.teamResultCell)}>
          {isCurrentUserParticipant ? (
            <CurrentUserArrow rotate={isExpanded} />
          ) : (
            <ParticipantArrow rotate={isExpanded} />
          )}
        </Grid>
        <Grid
          item
          xs={1}
          container
          justifyContent="center"
          style={{ paddingLeft: 0 }}
          className={classes.cell}
        >
          {placeByOrder ?? record.place}
        </Grid>
        <Grid item xs={6} className={classes.cell}>
          {record.name}
        </Grid>
        <Grid
          item
          container
          xs={4}
          justifyContent="flex-end"
          className={classes.cell}
        >
          {formatRecordResultExtended(challengeType, record.result)}
        </Grid>
      </Grid>
      <Grid item xs={11} className={cs(classes.cell, classes.teamContainer)}>
        {Boolean(record.metadata?.absolute) && isFull && (
          <>
            {t('absolute')}: {sec2hms(Math.round(record.metadata?.absolute!!))}
            <br />
          </>
        )}
        {Boolean(record.metadata?.prediction) && isFull && (
          <>
            {t('predictions')}: {sec2hms(record.metadata?.prediction!!)}
            <br />
          </>
        )}
        {Boolean(record.metadata?.penalty) && isFull && (
          <>
            {t('penalty')}: {record.metadata?.penalty}
            <br />
            <br />
          </>
        )}
        {visibleRecords.map((r) => (
          <Typography
            className={classes.clubLogoTeamCell}
            key={r.trackId}
            variant="body1"
            component="div"
          >
            <div className={classes.centeredRow}>
              <ParticipantInfo
                participant={r.participant}
                stepsInfo={record.metadata?.steps}
                googleConnected={
                  record.metadata?.googleConnected?.[r.participant.id]
                }
              />
            </div>
          </Typography>
        ))}
        {visibleRecords.length !== record.records.length && (
          <Typography variant="body1">
            {t('moreParticipants', {
              participants: record.records.length - visibleRecords.length,
            })}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((muiTheme) => ({
  cell: {
    alignItems: 'center',
    display: 'flex',
  },
  container: {
    width: '100%',
    margin: 0,
    fontSize: '0.9rem',
    fontWeight: 500,
    padding: '0.5rem 0',
  },
  info: {
    marginTop: '0.2rem',
    fontSize: '0.75rem',
    '&>div': {
      padding: '0.2rem 0',
    },
  },
  notFinished: {
    background: '#eeeeee',
  },
  parameters: {
    marginTop: '0.2rem',
    fontSize: '0.75rem',
    '&>div': {
      padding: '0.2rem 0',
    },
  },
  clubLogo: {
    maxHeight: '2.5rem',
    maxWidth: '100%',
  },
  clubLogoCell: {
    overflowX: 'auto',
    padding: 0,
  },
  clubLogoTeamCell: {
    display: 'flex',
    flexAlign: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  clubLogoTeam: {
    maxHeight: '2rem',
    maxWidth: '10rem',
    marginRight: '1rem',
  },
  teamResultCell: {
    marginBottom: '1rem',
  },
  crown: {
    width: '1.5rem',
    height: '1.5rem',
    marginRight: '0.5rem',
  },
  centeredRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 0',
    flexDirection: 'row',
  },
  badgeContainer: {
    display: 'flex',
    padding: '0.75rem 0',
    [muiTheme.breakpoints.down('md')]: {
      flexFlow: 'wrap',
    },
  },
  badge: {
    marginRight: '0.5rem',
    [muiTheme.breakpoints.down('md')]: {
      marginBottom: '0.5rem',
    },
  },
  alignEnd: {
    justifyContent: 'flex-end',
  },
  teamContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));
